import type { GameProfileArt } from '../../../assets/asset-manifest.js';
import { PLATE_GAME_NAME } from '../../../styles/plate-type.js';

/**
 * The illustrated header every game card on the profile shares (APP_SPEC
 * §13.3): the game's catalog image reused as a banner, its large emblem on
 * the LEFT and its name immediately to the RIGHT. The arrangement is
 * identical for every game so the cards read as one set.
 *
 * The banner runs full bleed to the card's gold trim — no border, no cream
 * margin, and no band of the dark shell around it. The game's artwork is
 * framed by the trim itself; the dark shell reads lower down, around the
 * parchment panel and in the footing.
 *
 * The header's own box is only as tall as that emblem needs: the banner is
 * the group's setting, so it is sized from the emblem rather than the emblem
 * being centred in whatever height the illustration would like.
 *
 * The art is pulled back to a lower key so the emblem and title read as the
 * card's subject and the illustration as its setting — but it is pulled back
 * by *brightness*, never by greyscale or a flat black sheet, so the forest
 * green, teal, brass and parchment stay plainly visible. A vignette softens
 * the four edges into the surrounding frame so the banner belongs to the
 * card rather than sitting in it as a pasted rectangle, its lower edge is
 * masked so the artwork dissolves into the card's dark shell instead of
 * ending on a hard line, and a left-weighted gradient buys the title its
 * contrast locally, fading out well before the middle of the picture. The
 * crop is a plain `cover` fit — never zoomed past it — anchored per game by
 * `art.bannerPosition` (declared with the artwork in the web game registry,
 * so nothing branches on a `gameId` here — CLAUDE.md §2).
 */
export interface GameCardHeaderProps {
  name: string;
  art: GameProfileArt;
  headingLevel?: 2 | 3;
}

/**
 * How the banner's decorative layers fade out at their lower edge.
 *
 * The dissolve is confined to the final ~7% — the band the parchment panel
 * laps over (the panel is pulled up 8px into a 104–124px banner, so its top
 * edge lands at roughly 93% of the banner's height). Anything earlier than
 * that shows as a wedge of dark shell directly above the panel, which is not
 * what the fade is for: the artwork should run at full strength right down to
 * the panel. What the fade actually covers is the narrow strip either side of
 * the panel, where the banner is not lapped and would otherwise end on a hard
 * line against the card's dark ground.
 */
const BANNER_FADE = 'linear-gradient(to bottom, #000 93%, rgb(0 0 0 / 0) 100%)';

export function GameCardHeader({ name, art, headingLevel = 3 }: GameCardHeaderProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3';

  // The emblem + title are one header group, seated close to the card's gold
  // trim on the left and vertically centred in a banner sized to them: the
  // height below is set from the emblem it carries, and the left inset is a
  // single padding value the emblem and title share — no transform, no
  // negative margin.
  return (
    <div className="relative z-[2] flex h-[118px] items-center gap-3 overflow-hidden pb-1.5 pl-3 pr-4 tablet:h-[134px] tablet:gap-3.5 tablet:pl-3.5 desktop:h-[146px]">
      {/* Every decorative layer shares one mask so the banner dissolves into
          the card's own dark gradient at its lower edge instead of ending on a
          hard horizontal cut. Masking (rather than laying a matching dark
          gradient over the art) means the fade resolves to whatever the card
          is actually painted with, so it stays seamless if that changes. The
          emblem and title sit outside this wrapper and are never faded. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          maskImage: BANNER_FADE,
          WebkitMaskImage: BANNER_FADE,
        }}
      >
        {/* The artwork itself: `cover`, anchored by the game, held at a low key. */}
        <div
          className="absolute inset-0 bg-no-repeat [filter:saturate(0.94)_brightness(0.66)]"
          style={{
            backgroundImage: `url("${art.cardBackground}")`,
            backgroundSize: art.bannerSize,
            backgroundPosition: art.bannerPosition,
          }}
        />
        {/* Localised title gradient — dark under the emblem/title, gone by the
            middle of the band, so the right two-thirds of the illustration is
            untouched. */}
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgb(0_18_14/0.72)_0%,rgb(0_18_14/0.44)_28%,rgb(0_18_14/0.12)_55%,transparent_76%)]" />
        {/* Vignette: melts the side and top edges into the gold trim. */}
        <div className="absolute inset-0 shadow-[inset_0_0_22px_8px_rgb(3_20_15/0.7)]" />
      </div>
      <img
        src={art.icon}
        alt=""
        aria-hidden="true"
        width={216}
        height={216}
        // The emblem art is a full-bleed 216px tile with no transparent
        // margin of its own (`pnpm assets:cards`), so its rendered box *is*
        // the visible glyph: the only space around it is the padding above,
        // and it is sized to take ~2/3 of the banner's height at every
        // breakpoint rather than sitting as a small mark in a tall band.
        className="relative h-[76px] w-[76px] shrink-0 object-contain [filter:drop-shadow(0_3px_10px_rgb(0_0_0/0.7))] tablet:h-[90px] tablet:w-[90px] desktop:h-[98px] desktop:w-[98px]"
        draggable={false}
      />
      <Heading
        className={`relative ${PLATE_GAME_NAME} text-textOnDark [text-shadow:0_2px_7px_rgb(0_0_0/0.7)]`}
      >
        {name}
      </Heading>
    </div>
  );
}
